import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import {
  ClearAllTrashDocument,
  DeleteTrashItemPermanentlyDocument,
  GetTrashDocument,
  RestoreTrashItemDocument,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";

/** Trash retention window enforced by core — rows auto-delete after this. */
export const TRASH_RETENTION_DAYS = 30;

/**
 * Days left before a trashed row auto-deletes. Derived from `Issue.trashedAt`,
 * so it is computed at render time and never carried on the row.
 */
export function trashDaysRemaining(trashedAt: string | null | undefined): number {
  if (!trashedAt) return TRASH_RETENTION_DAYS;
  const elapsedDays = Math.floor(
    (Date.now() - new Date(trashedAt).getTime()) / (1000 * 60 * 60 * 24),
  );
  return Math.max(0, TRASH_RETENTION_DAYS - elapsedDays);
}

/** Extract the Relay connection from a GetTrash query result. */
export const getTrashConnection = (data: any) => data?.trash;

export function useTrashMutations() {
  const [restoreMutation] = useMutation(RestoreTrashItemDocument);
  const [deleteMutation] = useMutation(DeleteTrashItemPermanentlyDocument);
  const [clearMutation] = useMutation(ClearAllTrashDocument);

  const restoreItem = useCallback(
    async (itemId: string, itemType: string = "Issue") => {
      try {
        await restoreMutation({
          variables: { itemId, itemType },
          update(cache) {
            cache.evict({ fieldName: "trash" });
            cache.gc();
          },
        });
        toastManager.add({ title: "Item restored", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to restore item");
      }
    },
    [restoreMutation],
  );

  const deleteItemPermanently = useCallback(
    async (itemId: string, itemType: string = "Issue") => {
      try {
        await deleteMutation({
          variables: { itemId, itemType },
          update(cache, { data }) {
            const result = data?.deleteTrashItemPermanently;
            cache.evict({ fieldName: "trash" });
            if (result?.__typename === "DeleteTrashItemSuccess") {
              cache.evict({ id: `Issue:${result.itemId}` });
            }
            cache.gc();
          },
        });
        toastManager.add({ title: "Item permanently deleted", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to delete item");
      }
    },
    [deleteMutation],
  );

  const clearAllTrash = useCallback(
    async (where?: { search?: string }) => {
      try {
        const { data } = await clearMutation({
          variables: { where },
          update(cache) {
            cache.evict({ fieldName: "trash" });
            cache.gc();
          },
        });
        const result = data?.clearAllTrash;
        if (result?.__typename === "ClearAllTrashSuccess") {
          toastManager.add({ title: "Trash cleared", type: "success" });
          return result.deletedCount as number;
        }
        return 0;
      } catch (err) {
        toastMutationError(err, "Failed to clear trash");
        return 0;
      }
    },
    [clearMutation],
  );

  return { restoreItem, deleteItemPermanently, clearAllTrash };
}
